import { getEtaStopDisplayState } from './etaStopDisplayState';

const toPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Absence-aware variant: Number(null) and Number('') are both a finite zero,
// which would silently turn an unavailable cumulative field into "0 minutes".
// Values used as subtraction inputs must distinguish absent from zero.
const toPresentFiniteNumber = (value) => (
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    ? null
    : toFiniteNumber(value)
);

const getBookingId = (booking = {}) => toPositiveNumber(booking.booking_id ?? booking.id);

const getWaypointId = (value) => toPositiveNumber(value);

const normalizeStatus = (value) => (
  typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : ''
);

const inactiveBookingStatuses = new Set(['canceled', 'cancelled', 'no_show']);
const canceledTripStatuses = new Set(['canceled', 'cancelled']);
const staticDistanceTripStatuses = new Set(['scheduled', 'dispatched']);

export const isActionableRiderBooking = (booking = {}) => {
  const boardingStatus = normalizeStatus(booking.boarding_status);
  const tripBookingStatus = normalizeStatus(booking.trip_booking_status);
  const bookingStatus = normalizeStatus(booking.booking_status ?? booking.status);

  if (boardingStatus === 'no_show') {
    return false;
  }

  if (tripBookingStatus === 'completed') {
    return true;
  }

  return !inactiveBookingStatuses.has(tripBookingStatus)
    && !inactiveBookingStatuses.has(bookingStatus);
};

const normalizeRiderBooking = (booking = {}) => ({
  ...booking,
  booking_id: getBookingId(booking),
  pickup_stop_id: getWaypointId(booking.pickup_stop_id),
  dropoff_stop_id: getWaypointId(booking.dropoff_stop_id),
  riders: Number(booking.riders) || 0,
  luggages: Number(booking.luggages) || 0
});

export const normalizeRiderBookings = (tripData = {}) => {
  const canonicalBookings = Array.isArray(tripData?.user_bookings)
    ? tripData.user_bookings
    : [];
  const compatibilityBooking = tripData?.user_booking ? [tripData.user_booking] : [];
  const seen = new Set();

  return [...canonicalBookings, ...compatibilityBooking]
    .map(normalizeRiderBooking)
    .filter((booking) => {
      if (!booking.booking_id || !isActionableRiderBooking(booking) || seen.has(booking.booking_id)) {
        return false;
      }
      seen.add(booking.booking_id);
      return true;
    });
};

const getStopState = (stop = {}) => {
  if (!stop) {
    return 'unknown';
  }
  if (stop.waypoint_departure_time || stop.departure_time || stop.is_passed === true) {
    return 'passed';
  }
  if (stop.waypoint_arrival_time || stop.arrival_time) {
    return 'dwelling';
  }
  return 'pending';
};

const getDisplayStops = (tripData = {}) => (
  Array.isArray(tripData?.effective_stops) && tripData.effective_stops.length > 0
    ? tripData.effective_stops
    : (Array.isArray(tripData?.stops) ? tripData.stops : [])
);

export const findTripStop = (tripData = {}, waypointId) => {
  const normalizedWaypointId = Number(waypointId);
  return getDisplayStops(tripData).find(
    (stop) => Number(stop?.waypoint_id) === normalizedWaypointId
  ) || null;
};

export const deriveRiderTripPhase = ({ tripData = {}, booking = null } = {}) => {
  if (!booking) {
    return 'unknown';
  }
  const tripStatus = normalizeStatus(tripData?.status);
  if (tripStatus === 'completed') {
    return 'completed';
  }
  if (canceledTripStatuses.has(tripStatus)) {
    return 'canceled';
  }

  const pickupStop = findTripStop(tripData, booking.pickup_stop_id);
  const dropoffStop = findTripStop(tripData, booking.dropoff_stop_id);
  const pickupState = getStopState(pickupStop);
  const dropoffState = getStopState(dropoffStop);

  // Arrival without departure is a dwelling state. At pickup that means the
  // rider is boarding; at dropoff it means alighting, not remaining travel ETA.
  if (dropoffState === 'passed') {
    return 'completed';
  }
  if (dropoffState === 'dwelling') {
    return 'dropoffDwelling';
  }
  if (pickupState === 'passed') {
    return 'onboard';
  }
  if (pickupState === 'dwelling') {
    return 'pickupDwelling';
  }
  if (pickupState === 'pending') {
    return 'prePickup';
  }
  return 'unknown';
};

const phasePriority = {
  prePickup: 0,
  pickupDwelling: 0,
  onboard: 1,
  dropoffDwelling: 1,
  completed: 2,
  canceled: 2,
  unknown: 3
};

export const selectDefaultRiderBooking = (bookings = [], tripData = {}) => {
  return [...bookings].sort((left, right) => {
    const leftPhase = deriveRiderTripPhase({ tripData, booking: left });
    const rightPhase = deriveRiderTripPhase({ tripData, booking: right });
    const phaseDiff = (phasePriority[leftPhase] ?? phasePriority.unknown)
      - (phasePriority[rightPhase] ?? phasePriority.unknown);
    if (phaseDiff !== 0) {
      return phaseDiff;
    }
    return (left.booking_id || 0) - (right.booking_id || 0);
  })[0] || null;
};

const statusTranslationKeys = {
  arriving: 'riderTrip.status.arriving',
  arrived: 'riderTrip.status.arrived',
  departed: 'riderTrip.status.departed',
  departing: 'riderTrip.status.departing',
  departure: 'riderTrip.status.departure',
  skipped: 'riderTrip.status.skipped',
  no_eta: 'riderTrip.status.noEta',
  unavailable: 'etaUnavailable',
  pending: 'riderTrip.status.pending',
  eta: 'riderTrip.status.eta'
};

const getStatusTranslationKey = (status) => (
  statusTranslationKeys[status] || statusTranslationKeys.pending
);

const findRelevantStop = (relevantStops = [], waypointId) => (
  relevantStops.find((stop) => Number(stop?.waypoint_id) === Number(waypointId)) || null
);

const getRelevantStopIndex = (relevantStops = [], waypointId) => (
  relevantStops.findIndex((stop) => Number(stop?.waypoint_id) === Number(waypointId))
);

const getStopDisplayState = ({
  stop,
  relevantStops,
  displayData,
  tripStatus,
  waypointStates
}) => {
  const index = relevantStops.indexOf(stop);
  const previousVisibleStop = index > 0 ? relevantStops[index - 1] : null;

  return getEtaStopDisplayState({
    stop,
    waypointState: waypointStates?.[stop?.waypoint_id],
    previousVisibleStop,
    previousWaypointState: previousVisibleStop ? waypointStates?.[previousVisibleStop.waypoint_id] : null,
    displayData,
    tripStatus,
    index: index >= 0 ? index : 0,
    totalStops: relevantStops.length || 1
  });
};

const isCountablePendingStop = ({ stop, displayState }) => (
  stop
  && stop.point_type !== 'waypoint'
  && stop.point_type !== 'origin'
  && !displayState.isDepartureLocation
  && !displayState.isSkipped
  && !displayState.isPassed
);

const countStopsBeforeIndex = ({
  relevantStops,
  targetIndex,
  displayData,
  tripStatus,
  waypointStates
}) => {
  if (targetIndex <= 0) {
    return 0;
  }

  return relevantStops.slice(0, targetIndex).filter((stop) => {
    const displayState = getStopDisplayState({
      stop,
      relevantStops,
      displayData,
      tripStatus,
      waypointStates
    });
    return isCountablePendingStop({ stop, displayState });
  }).length;
};

const countStopsBetweenIndexes = ({
  relevantStops,
  startIndex,
  endIndex,
  displayData,
  tripStatus,
  waypointStates
}) => {
  if (startIndex < 0 || endIndex <= startIndex) {
    return 0;
  }

  return relevantStops.slice(startIndex + 1, endIndex).filter((stop) => {
    const displayState = getStopDisplayState({
      stop,
      relevantStops,
      displayData,
      tripStatus,
      waypointStates
    });
    return isCountablePendingStop({ stop, displayState });
  }).length;
};

const getDisplayDistanceMeters = (row, displayState) => {
  const value = displayState?.isLiveEtaView
    ? row?.distance_meters_from_current
    : (row?.distance_meters_from_current ?? row?.dynamic_distance_meters);
  const numericValue = toPresentFiniteNumber(value);
  return numericValue !== null && numericValue >= 0 ? numericValue : null;
};

const getStaticSegmentDistanceMeters = (stop) => {
  const value = stop?.dynamic_distance_meters;
  // Number(null) and Number('') are both zero, but neither represents a
  // persisted static leg. Preserve that distinction before numeric parsing.
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }
  return toFiniteNumber(value);
};

const getRideDistanceMeters = ({
  relevantStops,
  pickupIndex,
  dropoffIndex,
  tripStatus
}) => {
  if (pickupIndex < 0 || dropoffIndex <= pickupIndex) {
    return null;
  }

  const rideSegments = relevantStops.slice(pickupIndex + 1, dropoffIndex + 1);

  // Before the trip starts, these are static route-segment legs. A partial sum
  // would disagree with the map and ETA list, so show no ride distance until
  // every leg has an authoritative static value. Live trips retain their
  // existing provider/decayed behavior, where temporarily missing legs may be
  // omitted while tracking recovers.
  if (staticDistanceTripStatuses.has(normalizeStatus(tripStatus))) {
    const staticDistances = rideSegments.map(getStaticSegmentDistanceMeters);
    if (staticDistances.some((distance) => distance === null || distance < 0)) {
      return null;
    }
    const distance = staticDistances.reduce((sum, segmentDistance) => sum + segmentDistance, 0);
    return distance > 0 ? distance : null;
  }

  // Dynamic segment distance is already assembled by the ETA list hook,
  // including hidden route waypoints. Sum those visible row segments instead of
  // re-reading raw ETA payloads or cumulative trip fields.
  const distance = rideSegments.reduce((sum, stop) => {
    const segmentDistance = toFiniteNumber(stop?.dynamic_distance_meters);
    return segmentDistance !== null && segmentDistance > 0 ? sum + segmentDistance : sum;
  }, 0);

  return distance > 0 ? distance : null;
};

const getNextRelevantStop = ({
  relevantStops,
  startIndex,
  displayData,
  tripStatus,
  waypointStates
}) => {
  if (startIndex < 0) {
    return null;
  }

  return relevantStops.slice(startIndex + 1).find((stop) => {
    const displayState = getStopDisplayState({
      stop,
      relevantStops,
      displayData,
      tripStatus,
      waypointStates
    });
    return isCountablePendingStop({ stop, displayState });
  }) || null;
};

const getWaypointStateTimestamp = (waypointStates = {}, waypointId) => (
  waypointStates?.[waypointId]?.timestamp || null
);

const getPickupDepartureTime = ({ row, displayState, waypointStates }) => {
  if (!row || !displayState) {
    return null;
  }

  const persistedTime = row.waypoint_departure_time
    ?? row.departure_time
    ?? row.actual_departure_time;
  if (persistedTime) {
    return persistedTime;
  }

  // Transient ETA-list states can carry the only reliable timestamp before the
  // persisted stop row catches up. Use it only when the shared lifecycle state
  // already says the pickup is leaving or passed.
  if (displayState.status === 'departing' || displayState.isPassed) {
    return getWaypointStateTimestamp(waypointStates, row.waypoint_id);
  }

  return null;
};

const getDropoffArrivalTime = ({ row, displayState, waypointStates }) => {
  if (!row || !displayState) {
    return null;
  }

  const persistedTime = row.waypoint_arrival_time
    ?? row.arrival_time
    ?? row.actual_arrival_time;
  if (persistedTime) {
    return persistedTime;
  }

  if (displayState.status === 'arrived' || displayState.isPassed || displayState.isDwelling) {
    return getWaypointStateTimestamp(waypointStates, row.waypoint_id);
  }

  return null;
};

const pushStatusOrEtaMetric = (metrics, labelKey, row, displayState, isStalePresentation = false) => {
  if (!row || !displayState) {
    return;
  }

  // Live arrival timestamps are display-only companions. The ETA duration
  // remains owned by eta_minutes_from_now via displayEtaMinutes. While the
  // location feed is lost the displayed number is a frozen snapshot, so the
  // live arrival clock is replaced by an explicit "last known" caption instead
  // of a companion time the rider would read as still updating.
  const liveArrivalTimeCompanion = isStalePresentation
    ? {
      companionType: 'translationKey',
      companionValue: 'riderTrip.stale.lastKnownCaption'
    }
    : (displayState.isLiveEtaView && row.estimated_arrival_time
      ? {
        companionType: 'time',
        companionValue: row.estimated_arrival_time
      }
      : {});

  if (displayState.status === 'eta') {
    metrics.push({
      labelKey,
      type: 'durationMinutes',
      value: displayState.displayEtaMinutes,
      ...liveArrivalTimeCompanion
    });
    return;
  }

  if (displayState.status === 'planned_time') {
    metrics.push({
      labelKey,
      type: 'time',
      value: row.estimated_arrival_time
    });
    return;
  }

  metrics.push({
    labelKey,
    type: 'status',
    status: displayState.status,
    value: getStatusTranslationKey(displayState.status),
    ...(displayState.status === 'arriving' ? liveArrivalTimeCompanion : {})
  });
};

const pushDistanceMetric = (metrics, labelKey, value) => {
  if (value !== null && value >= 0) {
    metrics.push({ labelKey, type: 'distanceMeters', value });
  }
};

const pushTimeMetric = (metrics, labelKey, value) => {
  if (value) {
    metrics.push({ labelKey, type: 'time', value });
  }
};

const pushStopsBeforePickupMetric = ({
  metrics,
  relevantStops,
  pickupIndex,
  displayData,
  tripStatus,
  waypointStates
}) => {
  // Preserve the existing count authority while making the zero state explicit
  // for riders. A start/departure row before pickup is not a pickup stop, so
  // "Pickup is next" is clearer than displaying a bare numeric zero.
  const stopsBeforePickup = countStopsBeforeIndex({
    relevantStops,
    targetIndex: pickupIndex,
    displayData,
    tripStatus,
    waypointStates
  });

  metrics.push({
    labelKey: 'riderTrip.metric.stopsBeforePickup',
    type: stopsBeforePickup === 0 ? 'translationKey' : 'count',
    value: stopsBeforePickup === 0
      ? 'riderTrip.metricValue.pickupIsNext'
      : stopsBeforePickup
  });
};

// Distance-only presentations carry no ETA authority at all, so an operational
// delay notice would announce minutes the card never applies to a number.
const isDistanceOnlyDisplay = (displayData) => (
  displayData?.type === 'distance_only'
  || displayData?.etaDisabled === true
  || displayData?.source === 'none'
);

// The origin/start row has no arrival boundary (the backend rejects an arrival
// declaration at the first waypoint), so its only applicable delay is the
// departure boundary. Explicit start flags are not sufficient: after a topology
// change the effective origin can be an ordinary stop row with a nonzero
// canonical sequence, so the first non-skipped displayed row is treated as the
// origin too — the same origin-selection rule deriveLiveEtaPayload applies.
const isEffectiveOriginRow = (relevantStops = [], row = null) => {
  if (!row) {
    return false;
  }
  const firstUnskippedRow = relevantStops.find((stop) => (
    stop?.is_skipped !== true && stop?.is_manual_skip !== true
  ));
  return Boolean(firstUnskippedRow) && firstUnskippedRow === row;
};

const isOriginPickupRow = (row, displayState, relevantStops) => (
  displayState?.isStartRow === true
  || displayState?.isDepartureLocation === true
  || row?.point_type === 'origin'
  || isEffectiveOriginRow(relevantStops, row)
);

// One notice per phase, read from the backend-projected per-row cumulative
// fields. Those already include upstream delays declared at earlier waypoints,
// which the ETA list's per-declaration carrier placement does not.
const getDelayNoticeTarget = ({
  phase,
  pickupRow,
  pickupState,
  dropoffRow,
  dropoffState,
  relevantStops
}) => {
  if (phase === 'prePickup' || phase === 'pickupArriving') {
    return {
      row: pickupRow,
      displayState: pickupState,
      kind: 'pickup',
      boundary: isOriginPickupRow(pickupRow, pickupState, relevantStops) ? 'departure' : 'arrival'
    };
  }
  if (phase === 'boarding') {
    return { row: pickupRow, displayState: pickupState, kind: 'pickup', boundary: 'departure' };
  }
  if (phase === 'onboard' || phase === 'dropoffApproaching') {
    return { row: dropoffRow, displayState: dropoffState, kind: 'dropoff', boundary: 'arrival' };
  }
  // arrivedDropoff keeps the historical per-declaration detail in the ETA list.
  return null;
};

const normalizeWaypointOrderVersion = (value) => (
  Number.isInteger(value) && value >= 0 ? value : null
);

// Attribution is presentation garnish on top of the cumulative number, never a
// second delay computation: declarations only name/count the stops whose
// pending boundaries feed the target's cumulative projection. Any missing or
// mismatched proof fails closed to `null` (attribution unavailable), which
// keeps the un-named lump-sum wording — the number itself always stays owned
// by the backend-projected cumulative field.
const getDelayContributingStops = ({
  displayData,
  tripData,
  targetRow,
  targetBoundary,
  // Optional lower bound: only declarations strictly after this boundary
  // contribute. Used by the ride line so boundaries already covered by the
  // pickup line are not attributed twice. A named baseline that cannot be
  // located in effective order fails the whole attribution closed.
  baselineRow = null,
  baselineBoundary = null
}) => {
  const declarations = Array.isArray(displayData?.operationalDelayDeclarations)
    ? displayData.operationalDelayDeclarations
    : null;
  const declarationVersion = normalizeWaypointOrderVersion(
    displayData?.operationalDelayProjectionWaypointOrderVersion
  );
  const effectiveStops = Array.isArray(tripData?.effective_stops)
    ? tripData.effective_stops
    : null;
  const effectiveStopsVersion = normalizeWaypointOrderVersion(
    tripData?.effective_stops_waypoint_order_version
  );
  if (
    !declarations
    || !effectiveStops
    || declarationVersion === null
    || effectiveStopsVersion === null
    || declarationVersion !== effectiveStopsVersion
  ) {
    return null;
  }

  const positionsById = new Map();
  effectiveStops.forEach((stop, position) => {
    const waypointId = Number(stop?.waypoint_id);
    if (
      Number.isInteger(waypointId)
      && waypointId > 0
      && !positionsById.has(waypointId)
      && stop?.is_skipped !== true
      && stop?.is_manual_skip !== true
    ) {
      positionsById.set(waypointId, { position, name: stop?.waypoint_name || '' });
    }
  });

  const targetEntry = positionsById.get(Number(targetRow?.waypoint_id));
  if (!targetEntry) {
    return null;
  }

  const baselineEntry = baselineRow
    ? positionsById.get(Number(baselineRow?.waypoint_id))
    : null;
  if (baselineRow && !baselineEntry) {
    return null;
  }

  const contributorsById = new Map();
  for (const declaration of declarations) {
    const waypointId = Number(declaration?.waypoint_id);
    const delayMinutes = Number(declaration?.delay_minutes);
    if (
      !Number.isInteger(waypointId)
      || waypointId <= 0
      || !['arrival', 'departure'].includes(declaration?.boundary_kind)
      || !Number.isInteger(delayMinutes)
      || delayMinutes <= 0
      || declaration?.boundary_crossed !== false
    ) {
      continue;
    }
    const entry = positionsById.get(waypointId);
    if (!entry) {
      continue;
    }
    // Every pending boundary strictly before the target feeds its cumulative
    // projection. At the target itself, an arrival declaration always precedes
    // both boundaries, while a departure declaration only precedes departure.
    const beforeTarget = entry.position < targetEntry.position
      || (entry.position === targetEntry.position
        && (declaration.boundary_kind === 'arrival' || targetBoundary === 'departure'));
    // With a baseline, only boundaries strictly after it contribute. At the
    // baseline stop itself, its departure follows an arrival baseline; every
    // boundary at or before the baseline is already covered by the first line.
    const afterBaseline = !baselineEntry
      || entry.position > baselineEntry.position
      || (entry.position === baselineEntry.position
        && declaration.boundary_kind === 'departure'
        && baselineBoundary === 'arrival');
    if (beforeTarget && afterBaseline && !contributorsById.has(waypointId)) {
      contributorsById.set(waypointId, entry);
    }
  }

  return [...contributorsById.values()];
};

// Attribution-aware wording. A single proven contributor is named; several are
// pointed at the ETA list; anything unprovable keeps the plain lump-sum keys.
const getDelayNoticeMessage = ({ included, minutes, contributors, targetKind }) => {
  if (contributors && contributors.length === 1 && contributors[0].name) {
    return included
      ? { messageKey: 'riderTrip.delay.includedAtStop', messageValues: { minutes, stop: contributors[0].name } }
      : { messageKey: 'riderTrip.delay.pendingAtStop', messageValues: { count: minutes, stop: contributors[0].name } };
  }
  if (contributors && contributors.length > 1) {
    const multiKey = targetKind === 'dropoff'
      ? (included ? 'riderTrip.delay.includedMultiBeforeDropoff' : 'riderTrip.delay.pendingMultiBeforeDropoff')
      : (included ? 'riderTrip.delay.includedMultiBeforePickup' : 'riderTrip.delay.pendingMultiBeforePickup');
    return included
      ? { messageKey: multiKey, messageValues: { minutes } }
      : { messageKey: multiKey, messageValues: { count: minutes } };
  }
  return included
    ? { messageKey: 'operationalDelay.includedBadge', messageValues: { minutes } }
    : { messageKey: 'operationalDelay.genericPending', messageValues: { count: minutes } };
};

const buildDelayNotice = ({
  phase,
  pickupRow,
  pickupState,
  dropoffRow,
  dropoffState,
  displayData,
  relevantStops,
  tripData
}) => {
  if (isDistanceOnlyDisplay(displayData)) {
    return null;
  }

  const target = getDelayNoticeTarget({
    phase,
    pickupRow,
    pickupState,
    dropoffRow,
    dropoffState,
    relevantStops
  });
  if (!target?.row || target.displayState?.isSkipped) {
    return null;
  }

  const minutes = toFiniteNumber(
    target.boundary === 'arrival'
      ? target.row.cumulative_arrival_delay_minutes
      : target.row.cumulative_departure_delay_minutes
  );
  if (minutes === null || minutes <= 0) {
    return null;
  }

  // "Includes" may only be claimed when the displayed ETA authority proves the
  // same boundary: eta_minutes_with_operational_delay is arrival authority, so
  // a departure-boundary notice (and any row without that number) stays a
  // pending announcement. Absence must be tested before the numeric parse:
  // Number(null) and Number('') are both a finite zero, which would claim an
  // authority the row does not carry.
  const arrivalEtaAuthorityValue = target.row.eta_minutes_with_operational_delay;
  const hasArrivalEtaAuthority = target.boundary === 'arrival'
    && arrivalEtaAuthorityValue !== null
    && arrivalEtaAuthorityValue !== undefined
    && arrivalEtaAuthorityValue !== ''
    && Number.isFinite(Number(arrivalEtaAuthorityValue));

  const contributors = getDelayContributingStops({
    displayData,
    tripData,
    targetRow: target.row,
    targetBoundary: target.boundary
  });

  return {
    boundary: target.boundary,
    minutes,
    ...getDelayNoticeMessage({
      included: hasArrivalEtaAuthority,
      minutes,
      contributors,
      targetKind: target.kind
    })
  };
};

// Second delay line while the rider still waits at (or approaches/boards) the
// pickup: delay accruing AFTER the pickup line's boundary and before dropoff
// arrival — the pickup's own departure hold, mid-ride stops, and the dropoff
// arrival declaration. The minutes are the difference of two backend-projected
// cumulative fields, never a client-side declaration sum; if either side is
// unavailable the line is omitted. Always "planned" wording: no ETA displayed
// pre-pickup includes these minutes.
const buildRideDelayNotice = ({
  phase,
  pickupRow,
  pickupState,
  dropoffRow,
  dropoffState,
  displayData,
  relevantStops,
  tripData
}) => {
  if (phase !== 'prePickup' && phase !== 'pickupArriving' && phase !== 'boarding') {
    return null;
  }
  if (isDistanceOnlyDisplay(displayData)) {
    return null;
  }
  if (!pickupRow || !dropoffRow || pickupState?.isSkipped || dropoffState?.isSkipped) {
    return null;
  }

  // The baseline is exactly the first line's boundary so the two lines
  // partition the dropoff total without overlap: departure for the origin
  // pickup and during boarding, arrival otherwise.
  const baselineBoundary = phase === 'boarding'
    || isOriginPickupRow(pickupRow, pickupState, relevantStops)
    ? 'departure'
    : 'arrival';

  // Both sides of the subtraction must be genuinely present: an absent
  // baseline coerced to zero would announce the dropoff's entire cumulative
  // delay as after-pickup delay.
  const dropoffMinutes = toPresentFiniteNumber(dropoffRow.cumulative_arrival_delay_minutes);
  const baselineMinutes = toPresentFiniteNumber(
    baselineBoundary === 'arrival'
      ? pickupRow.cumulative_arrival_delay_minutes
      : pickupRow.cumulative_departure_delay_minutes
  );
  if (dropoffMinutes === null || baselineMinutes === null) {
    return null;
  }
  const minutes = dropoffMinutes - baselineMinutes;
  if (minutes <= 0) {
    return null;
  }

  const contributors = getDelayContributingStops({
    displayData,
    tripData,
    targetRow: dropoffRow,
    targetBoundary: 'arrival',
    baselineRow: pickupRow,
    baselineBoundary
  });

  if (contributors && contributors.length === 1 && contributors[0].name) {
    return {
      boundary: 'ride',
      minutes,
      messageKey: 'riderTrip.delay.afterPickupAtStop',
      messageValues: { count: minutes, stop: contributors[0].name }
    };
  }
  if (contributors && contributors.length > 1) {
    return {
      boundary: 'ride',
      minutes,
      messageKey: 'riderTrip.delay.afterPickupMulti',
      messageValues: { count: minutes }
    };
  }
  return {
    boundary: 'ride',
    minutes,
    messageKey: 'riderTrip.delay.afterPickup',
    messageValues: { count: minutes }
  };
};

export const buildRiderTripSummaryModel = ({
  selectedBooking = null,
  relevantStops = [],
  displayData = null,
  tripStatus = null,
  tripData = null,
  waypointStates = {},
  boardingTrackingEnabled = false,
  isStalePresentation = false
} = {}) => {
  if (!selectedBooking) {
    return null;
  }

  // The summary intentionally consumes the same relevantStops/displayData pair
  // as the detailed ETA list. That keeps status, distance, suppression, and
  // manual-correction behavior in one lifecycle path instead of duplicating ETA
  // interpretation here.
  const pickupRow = findRelevantStop(relevantStops, selectedBooking.pickup_stop_id);
  const dropoffRow = findRelevantStop(relevantStops, selectedBooking.dropoff_stop_id);
  const pickupIndex = getRelevantStopIndex(relevantStops, selectedBooking.pickup_stop_id);
  const dropoffIndex = getRelevantStopIndex(relevantStops, selectedBooking.dropoff_stop_id);
  const pickupState = pickupRow ? getStopDisplayState({
    stop: pickupRow,
    relevantStops,
    displayData,
    tripStatus,
    waypointStates
  }) : null;
  const dropoffState = dropoffRow ? getStopDisplayState({
    stop: dropoffRow,
    relevantStops,
    displayData,
    tripStatus,
    waypointStates
  }) : null;
  const pickupName = selectedBooking.pickup_stop_name || pickupRow?.waypoint_name || '';
  const dropoffName = selectedBooking.dropoff_stop_name || dropoffRow?.waypoint_name || '';
  const nextStopRow = getNextRelevantStop({
    relevantStops,
    startIndex: pickupIndex,
    displayData,
    tripStatus,
    waypointStates
  });
  const rideDistanceMeters = getRideDistanceMeters({
    relevantStops,
    pickupIndex,
    dropoffIndex,
    tripStatus
  });
  const metrics = [];
  const normalizedTripStatus = normalizeStatus(tripStatus);
  const pickupDepartureTime = getPickupDepartureTime({
    row: pickupRow,
    displayState: pickupState,
    waypointStates
  });
  const dropoffArrivalTime = getDropoffArrivalTime({
    row: dropoffRow,
    displayState: dropoffState,
    waypointStates
  });

  let phase = 'prePickup';
  if (normalizedTripStatus === 'completed' || dropoffState?.isPassed || dropoffState?.isDwelling) {
    phase = 'arrivedDropoff';
  } else if (pickupState?.isPassed || pickupState?.status === 'departing') {
    phase = dropoffState?.status === 'arriving' ? 'dropoffApproaching' : 'onboard';
  } else if (pickupState?.status === 'arrived') {
    phase = 'boarding';
  } else if (pickupState?.status === 'arriving') {
    phase = 'pickupArriving';
  }

  if (phase === 'prePickup' || phase === 'pickupArriving') {
    pushStatusOrEtaMetric(metrics, 'riderTrip.metric.pickupStatus', pickupRow, pickupState, isStalePresentation);
    pushDistanceMetric(metrics, 'riderTrip.metric.pickupDistance', getDisplayDistanceMeters(pickupRow, pickupState));
    // Stop counts are only meaningful when the same ETA-list row used as the
    // counting anchor is present. During hydration, omit the card instead of
    // presenting an invented zero.
    if (pickupIndex >= 0) {
      pushStopsBeforePickupMetric({
        metrics,
        relevantStops,
        pickupIndex,
        displayData,
        tripStatus,
        waypointStates
      });
    }
    pushDistanceMetric(metrics, 'riderTrip.metric.rideDistance', rideDistanceMeters);
  } else if (phase === 'boarding') {
    pushStatusOrEtaMetric(metrics, 'riderTrip.metric.pickupStatus', pickupRow, pickupState, isStalePresentation);
    pushDistanceMetric(metrics, 'riderTrip.metric.rideDistance', rideDistanceMeters);
  } else if (phase === 'onboard' || phase === 'dropoffApproaching') {
    pushTimeMetric(metrics, 'riderTrip.metric.pickupDeparted', pickupDepartureTime);
    pushStatusOrEtaMetric(metrics, 'riderTrip.metric.dropoffStatus', dropoffRow, dropoffState, isStalePresentation);
    pushDistanceMetric(metrics, 'riderTrip.metric.remainingDistance', getDisplayDistanceMeters(dropoffRow, dropoffState));
    if (pickupIndex >= 0 && dropoffIndex > pickupIndex) {
      metrics.push({
        labelKey: 'riderTrip.metric.stopsBeforeDropoff',
        type: 'count',
        value: countStopsBetweenIndexes({
          relevantStops,
          startIndex: pickupIndex,
          endIndex: dropoffIndex,
          displayData,
          tripStatus,
          waypointStates
        })
      });
    }
  } else if (phase === 'arrivedDropoff') {
    pushTimeMetric(metrics, 'riderTrip.metric.dropoffArrived', dropoffArrivalTime);
    pushDistanceMetric(metrics, 'riderTrip.metric.rideDistance', rideDistanceMeters);
  }

  const secondaryTime = phase === 'arrivedDropoff' ? dropoffArrivalTime : pickupDepartureTime;
  let secondaryKey = null;
  if (phase === 'boarding') {
    secondaryKey = boardingTrackingEnabled
      ? 'riderTrip.secondary.boardingQr'
      : 'riderTrip.secondary.boarding';
  } else if (phase === 'prePickup') {
    secondaryKey = 'riderTrip.secondary.prePickup';
  } else if (phase === 'pickupArriving') {
    secondaryKey = 'riderTrip.secondary.pickupArriving';
  } else if ((phase === 'onboard' || phase === 'dropoffApproaching') && secondaryTime) {
    secondaryKey = 'riderTrip.secondary.pickupDeparted';
  } else if (phase === 'arrivedDropoff' && secondaryTime) {
    secondaryKey = 'riderTrip.secondary.dropoffArrived';
  }

  const delayNotice = buildDelayNotice({
    phase,
    pickupRow,
    pickupState,
    dropoffRow,
    dropoffState,
    displayData,
    relevantStops,
    tripData
  });
  const rideDelayNotice = buildRideDelayNotice({
    phase,
    pickupRow,
    pickupState,
    dropoffRow,
    dropoffState,
    displayData,
    relevantStops,
    tripData
  });

  return {
    phase,
    delayNotice,
    rideDelayNotice,
    isStalePresentation: Boolean(isStalePresentation),
    pickupStatus: pickupState?.status || 'pending',
    dropoffStatus: dropoffState?.status || 'pending',
    primaryKey: `riderTrip.primary.${phase}`,
    primaryValues: {
      location: phase === 'dropoffApproaching' || phase === 'arrivedDropoff' ? dropoffName : pickupName,
      pickup: pickupName,
      dropoff: dropoffName,
      nextStop: nextStopRow?.waypoint_name || dropoffName
    },
    secondaryKey,
    secondaryValues: {
      time: secondaryTime
    },
    metrics,
    pickupRow,
    dropoffRow,
    nextStopRow
  };
};

export const getSelectedRiderBooking = ({ bookings = [], selectedBookingId = null, tripData = {} } = {}) => {
  const normalizedSelectedId = toPositiveNumber(selectedBookingId);
  return bookings.find((booking) => booking.booking_id === normalizedSelectedId)
    || selectDefaultRiderBooking(bookings, tripData);
};

export const projectSelectedRiderBookingOntoTrip = (tripData = {}, selectedBooking = null) => {
  if (!selectedBooking) {
    return tripData;
  }

  const applySelectedFlags = (stops = []) => stops.map((stop) => ({
    ...stop,
    is_user_pickup: Number(stop?.waypoint_id) === Number(selectedBooking.pickup_stop_id),
    is_user_dropoff: Number(stop?.waypoint_id) === Number(selectedBooking.dropoff_stop_id)
  }));

  return {
    ...tripData,
    user_booking: selectedBooking,
    stops: Array.isArray(tripData?.stops) ? applySelectedFlags(tripData.stops) : tripData?.stops,
    effective_stops: Array.isArray(tripData?.effective_stops)
      ? applySelectedFlags(tripData.effective_stops)
      : tripData?.effective_stops
  };
};

export const getRiderBookingLabel = (booking = {}, fallback = 'Booking') => {
  const routeLabel = [booking.pickup_stop_name, booking.dropoff_stop_name]
    .filter(Boolean)
    .join(' → ');
  if (booking.rider_name) {
    return routeLabel ? `${booking.rider_name}: ${routeLabel}` : booking.rider_name;
  }
  return routeLabel || `${fallback} #${booking.booking_id}`;
};
